// words.ts — (B107; planning 10-23 16:45, 18:05; Creative 2's C2.9-1) the customer's words, editable in Admin → Words.
//
//   w(key, params)  the words for a key: the store's current text (GET /api/words/resolved, the owner's or an operator's
//                   edit, else the registry's default) when it has the key, else the shared default — never blank.
//                   Placeholders are `<name>` (the contract's form), filled from `params`.
//   loadWords()     reads the resolved words (again when a minute old; a failed read keeps what was held).
//   useWords()      re-renders a page when they arrive.
//
// (B108; planning 21:15, 22:05) the defaults are contracts' PAGE_WORDS (C2.9-2 / C2.9-4: the 192 keys of B107's step 0,
// in its order, text and locks), from @vms/contracts/browser — one list, the same the store registers. B107's own copy is
// test/fixtures/page-words-b107.json; test/b108-shared-words.test.ts holds the two equal. Every page string in 3a–3f goes
// through w() (test/b107-words.test.ts scans those files, and every key it uses must be a shared one).
import { useEffect, useState } from "react";
import { PAGE_WORDS as SHARED_PAGE_WORDS, PAGE_LOCKED_WORD_KEYS } from "@vms/contracts/browser";

/** The shared page words, by key (contracts' PAGE_WORDS). */
export const PAGE_WORDS: Readonly<Record<string, string>> = Object.freeze(Object.fromEntries(SHARED_PAGE_WORDS.map(([k, t]) => [k, t])));
/** A page word's key: one of contracts' (the scan test checks each one used). */
export type PageWordKey = string;

/** (B112, B113) page words a drop PROPOSES before they are in contracts' list: the fallback after the shared list (a key
 *  the shared list has wins). Empty now: B112's nine (C2.13) and B113's eleven (C2.14) are shared, so they left it. */
export const PAGE_WORDS_PROPOSED: Readonly<Record<string, string>> = Object.freeze({});

/** 🔒 (planning 16:45 Q-D) the lines TikTok's Content Sharing Guidelines require: they can be reworded, never removed. */
export const PAGE_LOCKED: readonly PageWordKey[] = PAGE_LOCKED_WORD_KEYS;

/** Pure: a text's `<name>` placeholders filled from params (one not given is left as written, so a gap shows, never blank). */
export function fill(text: string, params: Record<string, string | number> = {}): string {
  return text.replace(/<([a-z_]+)>/g, (m, k: string) => (k in params ? String(params[k]) : m));
}

// ── the store's current words ──
let resolved: Record<string, string> = {};
let loading: Promise<void> | null = null;
let loadedAt = -Infinity;
const FRESH_MS = 60_000;   // (audit 10-23) a page opened later in the same tab reads them again (an edit shows within a minute)
const listeners = new Set<() => void>();

/** Pure: an answer of GET /api/words/resolved → the usable words (a text that isn't a non-blank string is dropped). */
export function usableWords(a: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!a || typeof a !== "object" || Array.isArray(a)) return out;
  for (const [k, v] of Object.entries(a as Record<string, unknown>)) if (typeof v === "string" && v.trim()) out[k] = v;
  return out;
}
/** (tests) set the store's words directly; {} clears them */
export function setResolvedWords(words: Record<string, string>): void { resolved = usableWords(words); listeners.forEach((l) => l()); }

/** The words for a key: the store's current text, else the built-in default; with its placeholders filled. */
export function w(key: PageWordKey, params?: Record<string, string | number>): string {
  // (B108) a key the shared list lacks shows itself, never blank; (B112) after the words this drop proposes
  return fill(resolved[key] ?? PAGE_WORDS[key] ?? PAGE_WORDS_PROPOSED[key] ?? key, params);
}

/** Read the store's words (again when the last read is a minute old); a failed read keeps the words held — the defaults at first: never blank. */
export function loadWords(get: (path: string) => Promise<unknown> = async (p) => (await import("@/api/client.ts")).apiGet<unknown>(p),
    now = Date.now()): Promise<void> {
  if (!loading || now - loadedAt >= FRESH_MS) {
    loadedAt = now;
    loading = get("/words/resolved?locale=en").then((a) => setResolvedWords(usableWords(a)))
      .catch(() => { /* catch-reason: the words held stay (the defaults at first); the page never waits on them */ });
  }
  return loading;
}

/** A page that shows these words: reads them once, and re-renders when they arrive. */
export function useWords(): void {
  const [, tick] = useState(0);
  useEffect(() => {
    const l = () => tick((n) => n + 1);
    listeners.add(l);
    void loadWords();
    return () => { listeners.delete(l); };
  }, []);
}
